import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUserPlus, 
    faUser, 
    faPhone, 
    faCalendar, 
    faSave, 
    faTimes, 
    faSpinner, 
    faCheckCircle, 
    faExclamationTriangle,
    faStore,
    faExchangeAlt,
    faTag,
    faChevronDown,
    faEnvelope,
    faList,
    faFileAlt,
    faCopy,
    faTrashAlt,
    faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import "./Styles/Form.css";


// Create translator instance (same as dashboard)
const createCustomerTranslator = () => {
    let cache = new Map();
    let preloadedLanguages = new Set(['en', 'ur', 'ar', 'es']);

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Customer Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        // Use dictionary for common translations first
        const dict = {
            'ur': {
                'Add Customer': 'کسٹمر شامل کریں',
                'Fill in the customer details below': 'نیچے کسٹمر کی تفصیلات بھریں',
                'Customer Name': 'کسٹمر کا نام',
                'Store Name': 'اسٹور کا نام',
                'Phone Number': 'فون نمبر',
                'Email': 'ای میل',
                'Date': 'تاریخ',
                'Category': 'کیٹیگری',
                'Select a category': 'کیٹیگری منتخب کریں',
                'Save Customer': 'کسٹمر محفوظ کریں',
                'Clear': 'صاف کریں',
                'Adding...': 'شامل کر رہے ہیں...',
                'Customer saved successfully!': 'کسٹمر کامیابی سے محفوظ ہو گیا!',
                'Failed to save customer': 'کسٹمر محفوظ کرنے میں ناکامی',
                'Network error occurred': 'نیٹ ورک کی خرابی',
                'Customer name is required': 'کسٹمر کا نام ضروری ہے',
                'Name must be at least 2 characters': 'نام کم از کم 2 حروف کا ہونا چاہیے',
                'Please enter a valid phone number': 'براہ کرم درست فون نمبر درج کریں',
                'Please enter a valid email address': 'براہ کرم درست ای میل ایڈریس درج کریں',
                'Please select a valid date': 'براہ کرم درست تاریخ منتخب کریں',
                'Success': 'کامیابی',
                'Error': 'خرابی',
                'Warning': 'انتباہ',
                'Select a shop first': 'پہلے ایک شاپ منتخب کریں',
                'No shop selected': 'کوئی شاپ منتخب نہیں',
                'Switch Shop': 'شاپ تبدیل کریں',
                'Shop switched successfully': 'شاپ کامیابی سے تبدیل ہو گئی',
                'Failed to switch shop': 'شاپ تبدیل کرنے میں ناکامی',
                'Loading shops...': 'شاپس لوڈ ہو رہی ہیں...',
                'No shops available': 'کوئی شاپ دستیاب نہیں',
                'Current': 'موجودہ',
                'Optional': 'اختیاری',
                'Regular': 'عام',
                'VIP': 'وی آئی پی',
                'Corporate': 'کارپوریٹ',
                'Wholesale': 'تھوک',
                'Retail': 'خوردہ',
                'New': 'نیا',
                'Returning': 'واپس آنے والا',
                'Premium': 'پریمیم',
                'Draft loaded': 'ڈرافٹ لوڈ ہو گیا',
                'Draft cleared': 'ڈرافٹ صاف ہو گیا',
                'Clear Form': 'فارم صاف کریں',
                'Save Draft': 'ڈرافٹ محفوظ کریں',
                'Drafts': 'ڈرافٹس',
                'Customers': 'کسٹمرز',
                'Saved Drafts': 'محفوظ کردہ ڈرافٹس',
                'Unnamed Customer': 'نامعلوم کسٹمر',
                'No drafts saved': 'کوئی ڈرافٹ محفوظ نہیں',
                'Save': 'محفوظ کریں',
                'Go Back': 'واپس جائیں',
                'View Drafts': 'ڈرافٹس دیکھیں',
                'Customers List': 'کسٹمرز کی فہرست',
                'Clear Form': 'فارم صاف کریں'
            },
            'ar': {
                'Add Customer': 'إضافة عميل',
                'Fill in the customer details below': 'املأ تفاصيل العميل أدناه',
                'Customer Name': 'اسم العميل',
                'Store Name': 'اسم المتجر',
                'Phone Number': 'رقم الهاتف',
                'Email': 'البريد الإلكتروني',
                'Date': 'التاريخ',
                'Category': 'الفئة',
                'Select a category': 'اختر فئة',
                'Save Customer': 'حفظ العميل',
                'Clear': 'مسح',
                'Adding...': 'جاري الإضافة...',
                'Customer saved successfully!': 'تم حفظ العميل بنجاح!',
                'Failed to save customer': 'فشل في حفظ العميل',
                'Network error occurred': 'حدث خطأ في الشبكة',
                'Customer name is required': 'اسم العميل مطلوب',
                'Name must be at least 2 characters': 'يجب أن يكون الاسم حرفين على الأقل',
                'Please enter a valid phone number': 'الرجاء إدخال رقم هاتف صحيح',
                'Please enter a valid email address': 'الرجاء إدخال بريد إلكتروني صحيح',
                'Please select a valid date': 'الرجاء اختيار تاريخ صحيح',
                'Success': 'نجاح',
                'Error': 'خطأ',
                'Warning': 'تحذير',
                'Select a shop first': 'اختر متجرًا أولاً',
                'No shop selected': 'لم يتم اختيار متجر',
                'Switch Shop': 'تبديل المتجر',
                'Shop switched successfully': 'تم تبديل المتجر بنجاح',
                'Failed to switch shop': 'فشل في تبديل المتجر',
                'Loading shops...': 'جاري تحميل المتاجر...',
                'No shops available': 'لا توجد متاجر متاحة',
                'Current': 'حالي',
                'Optional': 'اختياري',
                'Regular': 'عادي',
                'VIP': 'شخصية مهمة',
                'Corporate': 'شركات',
                'Wholesale': 'جملة',
                'Retail': 'تجزئة',
                'New': 'جديد',
                'Returning': 'عائد',
                'Premium': 'مميز',
                'Draft loaded': 'تم تحميل المسودة',
                'Draft cleared': 'تم مسح المسودة',
                'Clear Form': 'مسح النموذج',
                'Save Draft': 'حفظ المسودة',
                'Drafts': 'المسودات',
                'Customers': 'العملاء',
                'Saved Drafts': 'المسودات المحفوظة',
                'Unnamed Customer': 'عميل بدون اسم',
                'No drafts saved': 'لا توجد مسودات محفوظة',
                'Save': 'حفظ',
                'Go Back': 'رجوع',
                'View Drafts': 'عرض المسودات',
                'Customers List': 'قائمة العملاء',
                'Clear Form': 'مسح النموذج'
            },
            'es': {
                'Add Customer': 'Agregar Cliente',
                'Fill in the customer details below': 'Complete los detalles del cliente a continuación',
                'Customer Name': 'Nombre del Cliente',
                'Store Name': 'Nombre de la Tienda',
                'Phone Number': 'Número de Teléfono',
                'Email': 'Correo Electrónico',
                'Date': 'Fecha',
                'Category': 'Categoría',
                'Select a category': 'Seleccione una categoría',
                'Save Customer': 'Guardar Cliente',
                'Clear': 'Limpiar',
                'Adding...': 'Agregando...',
                'Customer saved successfully!': '¡Cliente guardado exitosamente!',
                'Failed to save customer': 'Error al guardar el cliente',
                'Network error occurred': 'Ocurrió un error de red',
                'Customer name is required': 'El nombre del cliente es requerido',
                'Name must be at least 2 characters': 'El nombre debe tener al menos 2 caracteres',
                'Please enter a valid phone number': 'Por favor ingrese un número de teléfono válido',
                'Please enter a valid email address': 'Por favor ingrese un correo electrónico válido',
                'Please select a valid date': 'Por favor seleccione una fecha válida',
                'Success': 'Éxito',
                'Error': 'Error',
                'Warning': 'Advertencia',
                'Select a shop first': 'Seleccione una tienda primero',
                'No shop selected': 'No se seleccionó ninguna tienda',
                'Switch Shop': 'Cambiar Tienda',
                'Shop switched successfully': 'Tienda cambiada exitosamente',
                'Failed to switch shop': 'Error al cambiar de tienda',
                'Loading shops...': 'Cargando tiendas...',
                'No shops available': 'No hay tiendas disponibles',
                'Current': 'Actual',
                'Optional': 'Opcional',
                'Regular': 'Regular',
                'VIP': 'VIP',
                'Corporate': 'Corporativo',
                'Wholesale': 'Mayoreo',
                'Retail': 'Menudeo',
                'New': 'Nuevo',
                'Returning': 'Recurrente',
                'Premium': 'Premium',
                'Draft loaded': 'Borrador cargado',
                'Draft cleared': 'Borrador limpiado',
                'Clear Form': 'Limpiar Formulario',
                'Save Draft': 'Guardar Borrador',
                'Drafts': 'Borradores',
                'Customers': 'Clientes',
                'Saved Drafts': 'Borradores Guardados',
                'Unnamed Customer': 'Cliente sin nombre',
                'No drafts saved': 'No hay borradores guardados',
                'Save': 'Guardar',
                'Go Back': 'Volver',
                'View Drafts': 'Ver Borradores',
                'Customers List': 'Lista de Clientes',
                'Clear Form': 'Limpiar Formulario'
            }
        };

        if (dict[targetLang] && dict[targetLang][text]) {
            return dict[targetLang][text];
        }

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        addCustomer: 'Add Customer',
        fillDetails: 'Fill in the customer details below',
        customerName: 'Customer Name',
        storeName: 'Store Name',
        phoneNumber: 'Phone Number',
        email: 'Email',
        date: 'Date',
        category: 'Category',
        selectCategory: 'Select a category',
        addCustomerButton: 'Save Customer',
        clearButton: 'Clear',
        adding: 'Adding...',
        customerAdded: 'Customer saved successfully!',
        failedAdd: 'Failed to save customer',
        networkError: 'Network error occurred',
        nameRequired: 'Customer name is required',
        nameTooShort: 'Name must be at least 2 characters',
        phoneInvalid: 'Please enter a valid phone number',
        emailInvalid: 'Please enter a valid email address',
        dateInvalid: 'Please select a valid date',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        selectShop: 'Select a shop first',
        noShopSelected: 'No shop selected',
        switchShop: 'Switch Shop',
        shopSwitched: 'Shop switched successfully',
        shopSwitchFailed: 'Failed to switch shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        optional: 'Optional',
        regular: 'Regular',
        vip: 'VIP',
        corporate: 'Corporate',
        wholesale: 'Wholesale',
        retail: 'Retail',
        new: 'New',
        returning: 'Returning',
        premium: 'Premium',
        draftLoaded: 'Draft loaded',
        draftCleared: 'Draft cleared',
        clearForm: 'Clear Form',
        saveDraft: 'Save Draft',
        drafts: 'Drafts',
        customers: 'Customers',
        savedDrafts: 'Saved Drafts',
        unnamedCustomer: 'Unnamed Customer',
        noDraftsSaved: 'No drafts saved',
        save: 'Save',
        goBack: 'Go Back',
        viewDrafts: 'View Drafts',
        customersList: 'Customers List'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const customerTranslator = createCustomerTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations.success || 'Success';
            case 'error': return translations.error || 'Error';
            case 'warning': return translations.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// Shop Selector Modal
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    return (
        <div className="ozui_modal-overlay" onClick={onClose}>
            <div className="ozui_modal" onClick={e => e.stopPropagation()}>
                <div className="ozui_modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="ozui_modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="ozui_modal-body">
                    {loading ? (
                        <div className="ozui_loading-state">{translations.loadingShops || 'Loading shops...'}</div>
                    ) : shops.length === 0 ? (
                        <div className="ozui_empty-state">{translations.noShops || 'No shops available'}</div>
                    ) : (
                        shops.map((shop) => (
                            <div 
                                key={shop.shop_id}
                                className={`ozui_shop-item ${currentShop?.shop_id === shop.shop_id ? 'ozui_active' : ''}`}
                                onClick={() => onSelectShop(shop)}
                            >
                                <div className="ozui_shop-info">
                                    <div className="ozui_shop-name">{shop.shop_name}</div>
                                    <div className="ozui_shop-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                </div>
                                {currentShop?.shop_id === shop.shop_id && (
                                    <div className="ozui_shop-badge">✓ {translations.current || 'Current'}</div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

const AddCustomer = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    
    // Add ref for customer name input
    const customerNameInputRef = useRef(null);
    
    const initialFormData = {
        customer_name: '',
        store_name: '',
        phone: '',
        email: '',
        date: new Date().toISOString().split('T')[0],
        category: '',
    };
    
    const [formData, setFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('ourzest_darkMode') === 'true';
    });
    const [currentThemeColor, setCurrentThemeColor] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [categorySearchTerm, setCategorySearchTerm] = useState('');
    const [showDraftsList, setShowDraftsList] = useState(false);
    const [savedDrafts, setSavedDrafts] = useState([]);
    
    const isSubmittingRef = useRef(false);
    const toastTimerRef = useRef(null);
    const formRef = useRef(null);
    const categoryDropdownRef = useRef(null);
    const submitButtonRef = useRef(null);
    const isInitialMount = useRef(true);
    const draftsDropdownRef = useRef(null);

    // Auto-focus on customer name input when form is opened
    useEffect(() => {
        // Focus on the customer name input
        if (customerNameInputRef.current) {
            customerNameInputRef.current.focus();
        }
    }, []);

    // Handle Tab key on category dropdown to close it and move focus
    const handleCategoryKeyDown = (e) => {
        if (e.key === 'Tab') {
            setShowCategoryDropdown(false);
            // Let the tab naturally move to next element
            return;
        }
    };

    const categoryOptions = [
        { value: 'regular', label: 'Regular' },
        { value: 'vip', label: 'VIP' },
        { value: 'other', label: 'Other' },
        // { value: 'corporate', label: 'Corporate' },
        // { value: 'wholesale', label: 'Wholesale' },
        // { value: 'retail', label: 'Retail' },
        // { value: 'new', label: 'New' },
        // { value: 'returning', label: 'Returning' },
        // { value: 'premium', label: 'Premium' }
    ];

    // Draft Management Functions
    const saveDraft = () => {
        if (!formData.customer_name && !formData.phone && !formData.email) {
            showToast('warning', 'Cannot save empty draft');
            return;
        }
        
        const draftId = Date.now();
        const draft = {
            id: draftId,
            ...formData,
            savedAt: new Date().toISOString(),
            shop_id: currentShop?.shop_id
        };
        
        const existingDrafts = JSON.parse(localStorage.getItem('customer_drafts') || '[]');
        existingDrafts.unshift(draft);
        const limitedDrafts = existingDrafts.slice(0, 20);
        localStorage.setItem('customer_drafts', JSON.stringify(limitedDrafts));
        
        showToast('success', 'Draft saved successfully!');
        loadDrafts();
    };

    const loadDrafts = () => {
        const drafts = JSON.parse(localStorage.getItem('customer_drafts') || '[]');
        const filteredDrafts = currentShop ? drafts.filter(d => d.shop_id === currentShop?.shop_id) : drafts;
        setSavedDrafts(filteredDrafts);
    };

    const loadDraft = (draft) => {
        setFormData({
            customer_name: draft.customer_name || '',
            store_name: draft.store_name || '',
            phone: draft.phone || '',
            email: draft.email || '',
            date: draft.date || new Date().toISOString().split('T')[0],
            category: draft.category || '',
        });
        setCategorySearchTerm(draft.category ? categoryOptions.find(c => c.value === draft.category)?.label || '' : '');
        setShowDraftsList(false);
        showToast('success', translations.draftLoaded || 'Draft loaded successfully!');
    };

    const deleteDraft = (draftId) => {
        const drafts = JSON.parse(localStorage.getItem('customer_drafts') || '[]');
        const filteredDrafts = drafts.filter(d => d.id !== draftId);
        localStorage.setItem('customer_drafts', JSON.stringify(filteredDrafts));
        loadDrafts();
        showToast('success', 'Draft deleted successfully!');
    };

    const goToCustomersList = () => {
        window.dispatchEvent(new CustomEvent('navigateToCustomers', { detail: { page: 'customerlist' } }));
    };

    // Translate category options when language changes
    useEffect(() => {
        const translateCategories = async () => {
            if (currentLanguage === 'en') {
                return;
            }
            
            for (let cat of categoryOptions) {
                const translatedLabel = await customerTranslator.translateText(cat.label, currentLanguage);
                cat.label = translatedLabel;
            }
        };
        
        translateCategories();
    }, [currentLanguage]);

    useEffect(() => {
        loadDrafts();
    }, [currentShop]);

    // Add this useEffect to position the drafts dropdown
    useEffect(() => {
        if (showDraftsList && draftsDropdownRef.current) {
            const button = draftsDropdownRef.current.querySelector('.drafts-btn');
            const dropdown = draftsDropdownRef.current.querySelector('.ozui-drafts-dropdown');
            
            if (button && dropdown) {
                const rect = button.getBoundingClientRect();
                dropdown.style.position = 'fixed';
                dropdown.style.top = `${rect.bottom + 8}px`;
                dropdown.style.right = `${window.innerWidth - rect.right}px`;
            }
        }
    }, [showDraftsList]);

    // Click outside for drafts dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (draftsDropdownRef.current && !draftsDropdownRef.current.contains(event.target)) {
                setShowDraftsList(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredCategories = categorySearchTerm.trim() === '' 
        ? categoryOptions 
        : categoryOptions.filter(cat => 
            cat.label.toLowerCase().includes(categorySearchTerm.toLowerCase())
          );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
                setShowCategoryDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
            isSubmittingRef.current = false;
        };
    }, []);

    // ============================================
    // THEME COLOR AUTO-SYNC WITH GRADIENT ORBS
    // ============================================
    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
            color = '#6366f1';
        }
        console.log('Applying theme to AddCustomer form:', color);
        document.documentElement.style.setProperty('--ozui-primary', color);
        document.documentElement.style.setProperty('--ozui-primary-dark', color);
        document.documentElement.style.setProperty('--ozui-primary-light', color);
        
        // Update gradient orb colors dynamically
        const updateOrbColors = () => {
            // Parse color to RGB
            let r, g, b;
            if (color.startsWith('#')) {
                r = parseInt(color.slice(1, 3), 16);
                g = parseInt(color.slice(3, 5), 16);
                b = parseInt(color.slice(5, 7), 16);
            } else {
                r = 99; g = 102; b = 241; // Default fallback
            }
            
            // Create or update style for gradient orbs
            let style = document.getElementById('dynamic-orb-styles');
            if (!style) {
                style = document.createElement('style');
                style.id = 'dynamic-orb-styles';
                document.head.appendChild(style);
            }
            
            style.textContent = `
                .ozui-header-bg-animation .gradient-orb.orb-1 {
                    background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.4) 0%, rgba(${r}, ${g}, ${b}, 0.1) 100%);
                }
                .ozui-header-bg-animation .gradient-orb.orb-2 {
                    background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.35) 0%, rgba(${r}, ${g}, ${b}, 0.08) 100%);
                }
                .ozui-header-bg-animation .gradient-orb.orb-3 {
                    background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.3) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%);
                }
                .ozui-header-bg-animation .gradient-orb.orb-4 {
                    background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${r}, ${g}, ${b}, 0.03) 100%);
                }
                .ozui-header-bg-animation .gradient-orb.orb-5 {
                    background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.02) 100%);
                }
            `;
        };
        
        updateOrbColors();
        setCurrentThemeColor(color);
    }, []);

    useEffect(() => {
        // Initial theme application
        const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
        applyTheme(savedTheme);
        
        // Function to force update orbs
        const forceOrbUpdate = () => {
            const currentTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
            if (currentTheme !== currentThemeColor) {
                applyTheme(currentTheme);
            } else {
                // Still ensure orbs are correct
                let r, g, b;
                if (currentTheme.startsWith('#')) {
                    r = parseInt(currentTheme.slice(1, 3), 16);
                    g = parseInt(currentTheme.slice(3, 5), 16);
                    b = parseInt(currentTheme.slice(5, 7), 16);
                } else {
                    r = 99; g = 102; b = 241;
                }
                
                let style = document.getElementById('dynamic-orb-styles');
                if (!style) {
                    style = document.createElement('style');
                    style.id = 'dynamic-orb-styles';
                    document.head.appendChild(style);
                }
                
                style.textContent = `
                    .ozui-header-bg-animation .gradient-orb.orb-1 {
                        background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.4) 0%, rgba(${r}, ${g}, ${b}, 0.1) 100%);
                    }
                    .ozui-header-bg-animation .gradient-orb.orb-2 {
                        background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.35) 0%, rgba(${r}, ${g}, ${b}, 0.08) 100%);
                    }
                    .ozui-header-bg-animation .gradient-orb.orb-3 {
                        background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.3) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%);
                    }
                    .ozui-header-bg-animation .gradient-orb.orb-4 {
                        background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${r}, ${g}, ${b}, 0.03) 100%);
                    }
                    .ozui-header-bg-animation .gradient-orb.orb-5 {
                        background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.02) 100%);
                    }
                `;
            }
        };
        
        // Listen for storage changes
        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                const newTheme = e.newValue || '#6366f1';
                console.log('Theme changed via storage:', newTheme);
                applyTheme(newTheme);
            }
        };
        
        // Listen for custom theme change event
        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                console.log('Theme changed via event:', event.detail.theme);
                applyTheme(event.detail.theme);
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);
        
        // Poll for theme changes
        const interval = setInterval(() => {
            const storedTheme = localStorage.getItem('ourzest_theme');
            if (storedTheme && storedTheme !== currentThemeColor) {
                applyTheme(storedTheme);
            }
            forceOrbUpdate();
        }, 500);
        
        // DOM observer to ensure orbs are updated after re-renders
        const domObserver = new MutationObserver(() => {
            forceOrbUpdate();
        });
        
        domObserver.observe(document.body, {
            childList: true,
            subtree: true
        });
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
            clearInterval(interval);
            domObserver.disconnect();
        };
    }, [applyTheme, currentThemeColor]);

    // ============================================
    // DARK MODE AUTO-SYNC
    // ============================================
    useEffect(() => {
        const syncDarkMode = () => {
            const isDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDarkMode);
            
            const container = document.querySelector('.ozui-container');
            if (container) {
                if (isDarkMode) {
                    container.classList.add('ozui-dark');
                } else {
                    container.classList.remove('ozui-dark');
                }
            }
            
            // Also apply to body for global dark mode
            if (isDarkMode) {
                document.body.classList.add('ozui-dark');
                document.documentElement.classList.add('ozui-dark');
            } else {
                document.body.classList.remove('ozui-dark');
                document.documentElement.classList.remove('ozui-dark');
            }
        };
        
        syncDarkMode();
        
        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };
        
        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                const isDark = event.detail.darkMode;
                setDarkMode(isDark);
                
                const container = document.querySelector('.ozui-container');
                if (container) {
                    if (isDark) {
                        container.classList.add('ozui-dark');
                    } else {
                        container.classList.remove('ozui-dark');
                    }
                }
                
                if (isDark) {
                    document.body.classList.add('ozui-dark');
                    document.documentElement.classList.add('ozui-dark');
                } else {
                    document.body.classList.remove('ozui-dark');
                    document.documentElement.classList.remove('ozui-dark');
                }
            } else {
                syncDarkMode();
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);
        
        // Poll for dark mode changes every second (as fallback)
        const interval = setInterval(() => {
            const storedDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            if (storedDarkMode !== darkMode) {
                syncDarkMode();
            }
        }, 1000);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
            clearInterval(interval);
        };
    }, [darkMode]);

    // ============================================
    // LANGUAGE AUTO-SYNC
    // ============================================
    useEffect(() => {
        const loadTranslations = async (lang) => {
            console.log('Loading translations for language:', lang);
            const trans = await customerTranslator.getTranslations(lang);
            setTranslations(trans);
        };
        
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                console.log('Language changed via storage:', lang);
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };
        
        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    console.log('Language changed via event:', newLang);
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };
        
        // Initial load
        loadTranslations(currentLanguage);
        
        window.addEventListener('storage', handleLanguageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);
        
        // Poll for language changes every second (as fallback)
        const interval = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 1000);
        
        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // ============================================
    // RTL DIRECTION SYNC
    // ============================================
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const container = document.querySelector('.ozui-container');
        
        if (container) {
            if (isRTL) {
                container.classList.add('ozui-rtl');
                container.classList.remove('ozui-ltr');
            } else {
                container.classList.add('ozui-ltr');
                container.classList.remove('ozui-rtl');
            }
        }
        
        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    const submitCustomer = async (submitData) => {
        if (isSubmittingRef.current) return false;
        isSubmittingRef.current = true;

        const loggedInUsername = String(
            user?.username || user?.user_name || user?.login_username || ''
        ).trim();
        if (!loggedInUsername) {
            showToast('error', 'Your login username is unavailable. Please sign in again.');
            isSubmittingRef.current = false;
            return false;
        }

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_customers.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'add',
                    customer_name: submitData.customer_name,
                    store_name: submitData.store_name,
                    phone: submitData.phone,
                    email: submitData.email,
                    date: submitData.date,
                    category: submitData.category,
                    username: loggedInUsername,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', translations.customerAdded || 'Customer saved successfully!');
                setFormData(initialFormData);
                setCategorySearchTerm('');
                setErrors({});
                return true;
            } else {
                showToast('error', data.message || translations.failedAdd || 'Failed to save customer');
                return false;
            }
        } catch (error) {
            showToast('error', translations.networkError || 'Network error occurred');
            return false;
        } finally {
            setTimeout(() => {
                isSubmittingRef.current = false;
                setLoading(false);
            }, 1000);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmittingRef.current) return;
        if (!currentShop) {
            showToast('error', translations.selectShop || 'Please select a shop first');
            setShowShopSelector(true);
            return;
        }
        if (!validateForm()) return;
        
        setLoading(true);
        await submitCustomer(formData);
        setLoading(false);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.customer_name.trim()) {
            newErrors.customer_name = translations.nameRequired || 'Customer name is required';
        } else if (formData.customer_name.trim().length < 2) {
            newErrors.customer_name = translations.nameTooShort || 'Name must be at least 2 characters';
        }
        if (formData.phone && !/^[\d\s\-\+\(\)]{10,15}$/.test(formData.phone)) {
            newErrors.phone = translations.phoneInvalid || 'Please enter a valid phone number';
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = translations.emailInvalid || 'Please enter a valid email address';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const selectCategory = (category) => {
        setFormData(prev => ({ ...prev, category: category.value }));
        setCategorySearchTerm(category.label);
        setShowCategoryDropdown(false);
    };

    const handleClear = () => {
        setFormData(initialFormData);
        setCategorySearchTerm('');
        setErrors({});
        showToast('success', translations.draftCleared || 'Form cleared');
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', translations.shopSwitched || 'Shop switched successfully');
                setShowShopSelector(false);
            } else {
                showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
            }
        } catch (error) {
            showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
        }
    };

    useEffect(() => {
        const loadAvailableShops = async () => {
            if (!user) return;
            setLoadingShops(true);
            try {
                const shopsData = await getShopsForSwitch();
                setAvailableShops(shopsData.shops || []);
            } catch (error) {
                setAvailableShops([]);
            } finally {
                setLoadingShops(false);
            }
        };
        loadAvailableShops();
    }, [user, getShopsForSwitch]);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`ozui-container ${isRTL ? 'ozui-rtl' : 'ozui-ltr'} ${darkMode ? 'ozui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />
            
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Modern Animated Sticky Header */}
            <div className="ozui-sticky-header">
                <div className="ozui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozui-header-content">
                    <div className="ozui-header-left">
                        <div className="ozui-header-title">
                            <h1>{translations.addCustomer || 'Add Customer'}</h1>
                            <p>{translations.fillDetails || 'Fill in the customer details below'}</p>
                        </div>
                    </div>
                    
                    <div className="ozui-header-right">
                        {!currentShop && (
                            <button className="ozui-header-shop-btn" onClick={() => setShowShopSelector(true)}>
                                <FontAwesomeIcon icon={faExchangeAlt} />
                                <span>{translations.switchShop || 'Switch Shop'}</span>
                            </button>
                        )}
                        
                        <div className="ozui-header-actions">
                            <button 
                                className="ozui-action-btn clear-draft" 
                                onClick={handleClear}
                                disabled={loading}
                                title={translations.clearForm || 'Clear Form'}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} />
                                <span>{translations.clearButton || 'Clear'}</span>
                            </button>
                            
                            <button 
                                className="ozui-action-btn save-draft" 
                                onClick={saveDraft}
                                disabled={loading}
                                title={translations.saveDraft || 'Save as Draft'}
                            >
                                <FontAwesomeIcon icon={faCopy} />
                                <span>{translations.saveDraft || 'Save Draft'}</span>
                            </button>
                            
                            <div className="ozui-drafts-dropdown-container" ref={draftsDropdownRef}>
                                <button 
                                    className="ozui-action-btn drafts-btn" 
                                    onClick={() => setShowDraftsList(!showDraftsList)}
                                    title={translations.viewDrafts || 'View Drafts'}
                                >
                                    <FontAwesomeIcon icon={faFileAlt} />
                                    <span>{translations.drafts || 'Drafts'}</span>
                                    {savedDrafts.length > 0 && <span className="drafts-badge">{savedDrafts.length}</span>}
                                </button>
                                
                                {showDraftsList && (
                                    <div className="ozui-drafts-dropdown">
                                        <div className="drafts-header">
                                            <h4>{translations.savedDrafts || 'Saved Drafts'}</h4>
                                            <button onClick={() => setShowDraftsList(false)}>×</button>
                                        </div>
                                        <div className="drafts-list">
                                            {savedDrafts.length === 0 ? (
                                                <div className="no-drafts">{translations.noDraftsSaved || 'No drafts saved'}</div>
                                            ) : (
                                                savedDrafts.map(draft => (
                                                    <div key={draft.id} className="draft-item">
                                                        <div className="draft-info" onClick={() => loadDraft(draft)}>
                                                            <div className="draft-name">
                                                                {draft.customer_name || translations.unnamedCustomer || 'Unnamed Customer'}
                                                            </div>
                                                            <div className="draft-details">
                                                                {draft.phone && <span>{draft.phone}</span>}
                                                                {draft.email && <span>{draft.email}</span>}
                                                            </div>
                                                            <div className="draft-date">
                                                                {new Date(draft.savedAt).toLocaleDateString()}
                                                            </div>
                                                        </div>
                                                        <button 
                                                            className="delete-draft-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                deleteDraft(draft.id);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <form ref={formRef} onSubmit={handleSubmit} className="ozui-form">
                <div className="ozui-form-row">
                    <div className={`ozui-field ${errors.customer_name ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                ref={customerNameInputRef}
                                type="text"
                                name="customer_name"
                                id="customer_name"
                                value={formData.customer_name}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                autoFocus
                            />
                            <label htmlFor="customer_name">
                                <FontAwesomeIcon icon={faUser} />
                                {translations.customerName || 'Customer Name'} <span>*</span>
                            </label>
                        </div>
                        {errors.customer_name && <small>{errors.customer_name}</small>}
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="store_name"
                                id="store_name"
                                value={formData.store_name}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="store_name">
                                <FontAwesomeIcon icon={faStore} />
                                {translations.storeName || 'Store Name'} 
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                    </div>

                    <div className={`ozui-field ${errors.phone ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="tel"
                                name="phone"
                                id="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="phone">
                                <FontAwesomeIcon icon={faPhone} />
                                {translations.phoneNumber || 'Phone Number'} 
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                        {errors.phone && <small>{errors.phone}</small>}
                    </div>

                    <div className={`ozui-field ${errors.email ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="email"
                                name="email"
                                id="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="email">
                                <FontAwesomeIcon icon={faEnvelope} />
                                {translations.email || 'Email'} 
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                        {errors.email && <small>{errors.email}</small>}
                    </div>

                    <div className="ozui-field" ref={categoryDropdownRef}>
                        <div className="ozui-input-wrapper ozui-category-wrapper">
                            <input
                                type="text"
                                id="category"
                                value={categorySearchTerm}
                                onChange={(e) => {
                                    setCategorySearchTerm(e.target.value);
                                    setShowCategoryDropdown(true);
                                    if (formData.category && !categoryOptions.find(c => 
                                        c.label.toLowerCase() === e.target.value.toLowerCase()
                                    )) {
                                        setFormData(prev => ({ ...prev, category: '' }));
                                    }
                                }}
                                onFocus={() => setShowCategoryDropdown(true)}
                                onKeyDown={handleCategoryKeyDown}
                                placeholder=" "
                                disabled={loading}
                                autoComplete="off"
                            />
                            <label htmlFor="category">
                                <FontAwesomeIcon icon={faTag} />
                                {translations.category || 'Category'} 
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {showCategoryDropdown && (
                            <div className="ozui-category-dropdown">
                                {filteredCategories.length > 0 ? (
                                    filteredCategories.map(cat => (
                                        <div
                                            key={cat.value}
                                            className={`ozui-category-option ${formData.category === cat.value ? 'ozui-selected' : ''}`}
                                            onClick={() => selectCategory(cat)}
                                        >
                                            {cat.label}
                                        </div>
                                    ))
                                ) : (
                                    <div className="ozui-category-empty">No categories found</div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="date"
                                name="date"
                                id="date"
                                value={formData.date}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="date">
                                <FontAwesomeIcon icon={faCalendar} />
                                {translations.date || 'Date'}
                            </label>
                        </div>
                    </div>
                </div>
                
                {/* Form Actions - Save button moved here below the fields */}
                <div className="ozui-form-actions">
                    <button 
                        ref={submitButtonRef}
                        type="submit" 
                        className="ozui-btn ozui-btn-primary" 
                        onClick={handleSubmit}
                        disabled={loading || !currentShop}
                    >
                        {loading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>{translations.adding || 'Adding...'}</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} />
                                <span>{translations.addCustomerButton || 'Save Customer'}</span>
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddCustomer;
